import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, Chip, Checkbox } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ReleaseNotesEntry } from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import { markdownComponents, MARKDOWN_CONTENT_STYLE } from '../utils/markdownRenderUtils';
import { ReleaseNotesSubscribersPanel } from './ReleaseNotesSubscribersPanel';

export interface ReleaseNotesModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly customer: string;
  readonly releases: ReleaseNotesEntry[];
  readonly initialReleaseId?: string;
  /** Total release count on the server, when known (e.g. from a paginated feed's `total`). */
  readonly total?: number;
  /** Omit to hide the Word export button (requires a host-provided export implementation). */
  readonly onDownloadWord?: (release: ReleaseNotesEntry) => void;
  /** Omit to hide the PDF export button (requires a host-provided export implementation). */
  readonly onDownloadPdf?: (release: ReleaseNotesEntry) => void;
  /** Omit to hide the Notify button (requires a host-provided notification implementation). */
  readonly onNotify?: (release: ReleaseNotesEntry) => void;
  readonly showDrafts?: boolean;
  /** Omit to hide the "Show drafts" toggle. */
  readonly onToggleShowDrafts?: (show: boolean) => void;
  /** Whether more releases exist beyond the current `releases` page. */
  readonly hasMore?: boolean;
  /** Shows a loading row at the bottom of the release list while the next page fetches. */
  readonly isLoadingMore?: boolean;
  /** Called when the release list is scrolled near its bottom and `hasMore` is true (infinite scroll). */
  readonly onLoadMore?: () => void;
  readonly tenantId?: string;
  /** Bearer token for the Subscribers panel's calls (the app's own token — same one used elsewhere). Omit to hide the Subscribers button. */
  readonly subscribersToken?: string;
  /** Overrides `CTY_IDP_API_URL` for Subscribers panel calls. */
  readonly subscribersBaseUrl?: string;
}

/** Scrolled within this many pixels of the bottom triggers loading the next page. */
const LOAD_MORE_THRESHOLD_PX = 96;

function formatSignedOffAt(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const datePart = date.toLocaleString('en-US', { day: '2-digit', month: 'short' });
  const timePart = date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const offsetHours = Math.floor(absMinutes / 60);
  const offsetRemainder = absMinutes % 60;
  const offset = `GMT${sign}${offsetHours}:${String(offsetRemainder).padStart(2, '0')}`;
  return `${datePart}, ${timePart} ${offset}`;
}

function sourceTagLabel(source: ReleaseNotesEntry['source']): string {
  return source === 'customer' ? 'customer' : 'product';
}

function downloadMarkdownFile(release: ReleaseNotesEntry): void {
  const blob = new Blob([release.markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `ReleaseNotes_${release.name}.md`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

interface ReleaseHeaderActionsProps {
  readonly selectedRelease: ReleaseNotesEntry | undefined;
  readonly showDrafts?: boolean;
  readonly onToggleShowDrafts?: (show: boolean) => void;
  readonly onDownloadWord?: (release: ReleaseNotesEntry) => void;
  readonly onDownloadPdf?: (release: ReleaseNotesEntry) => void;
  readonly onNotify?: (release: ReleaseNotesEntry) => void;
  readonly onCopy: (release: ReleaseNotesEntry) => void;
  readonly showSubscribersButton: boolean;
  readonly onToggleSubscribers: () => void;
}

function ReleaseHeaderActions(props: ReleaseHeaderActionsProps) {
  const {
    selectedRelease,
    showDrafts,
    onToggleShowDrafts,
    onDownloadWord,
    onDownloadPdf,
    onNotify,
    onCopy,
    showSubscribersButton,
    onToggleSubscribers,
  } = props;
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2 flex-wrap w-full">
      {onToggleShowDrafts ? (
        <Checkbox size="sm" isSelected={!!showDrafts} onValueChange={onToggleShowDrafts}>
          {t('Show drafts')}
        </Checkbox>
      ) : null}
      {onDownloadWord ? (
        <CtyButton
          size="sm"
          variant="flat"
          color="primary"
          isDisabled={!selectedRelease}
          startContent={<Icon icon="mdi:file-word-outline" width="1.1em" height="1.1em" />}
          onPress={() => selectedRelease && onDownloadWord(selectedRelease)}
        >
          {t('Word')}
        </CtyButton>
      ) : null}
      {onDownloadPdf ? (
        <CtyButton
          size="sm"
          variant="flat"
          color="danger"
          isDisabled={!selectedRelease}
          startContent={<Icon icon="mdi:file-pdf-box" width="1.1em" height="1.1em" />}
          onPress={() => selectedRelease && onDownloadPdf(selectedRelease)}
        >
          {t('PDF')}
        </CtyButton>
      ) : null}
      {onNotify ? (
        <CtyButton
          size="sm"
          variant="flat"
          color="warning"
          isDisabled={!selectedRelease}
          startContent={<Icon icon="mdi:bell-outline" width="1.1em" height="1.1em" />}
          onPress={() => selectedRelease && onNotify(selectedRelease)}
        >
          {t('Notify')}
        </CtyButton>
      ) : null}
      <CtyButton
        size="sm"
        variant="flat"
        color="secondary"
        isDisabled={!selectedRelease}
        startContent={<Icon icon="mdi:content-copy" width="1.1em" height="1.1em" />}
        onPress={() => selectedRelease && onCopy(selectedRelease)}
      >
        {t('Copy')}
      </CtyButton>
      <CtyButton
        size="sm"
        variant="flat"
        color="default"
        isDisabled={!selectedRelease}
        startContent={<Icon icon="mdi:download-outline" width="1.1em" height="1.1em" />}
        onPress={() => selectedRelease && downloadMarkdownFile(selectedRelease)}
      >
        {t('Download .md')}
      </CtyButton>
      {showSubscribersButton ? (
        <CtyButton
          size="sm"
          variant="flat"
          color="success"
          className="ml-auto"
          startContent={<Icon icon="mdi:account-multiple-outline" width="1.1em" height="1.1em" />}
          onPress={onToggleSubscribers}
        >
          {t('Subscribers')}
        </CtyButton>
      ) : null}
    </div>
  );
}

interface ReleaseListItemProps {
  readonly release: ReleaseNotesEntry;
  readonly isSelected: boolean;
  readonly onSelect: (id: string) => void;
}

function ReleaseListItem({ release, isSelected, onSelect }: ReleaseListItemProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(release.id)}
      className={`w-full text-left px-3 py-2 border-b last:border-b-0 flex flex-col gap-1 ${isSelected ? 'bg-primary-50 border-l-4 border-l-primary' : 'hover:bg-default-50'
        }`}
    >
      <span className="text-sm font-semibold">{release.name}</span>
      <span className="text-xs text-default-500">{formatSignedOffAt(release.signed_off_at)}</span>
      <Chip size="sm" color={release.source === 'customer' ? 'primary' : 'default'} variant="flat" className="w-fit">
        {sourceTagLabel(release.source)}
      </Chip>
    </button>
  );
}

interface ReleaseListSidebarProps {
  readonly releases: ReleaseNotesEntry[];
  readonly selectedReleaseId: string | undefined;
  readonly total?: number;
  readonly isLoadingMore?: boolean;
  readonly onSelect: (id: string) => void;
  readonly onScroll: (event: React.UIEvent<HTMLDivElement>) => void;
}

function ReleaseListSidebar(props: ReleaseListSidebarProps) {
  const { releases, selectedReleaseId, total, isLoadingMore, onSelect, onScroll } = props;
  const { t } = useTranslation();
  const countLabel =
    total != null && total > releases.length ? `${releases.length} ${t('OF')} ${total}` : releases.length;

  return (
    <div className="w-64 flex-shrink-0 flex flex-col border rounded-md overflow-hidden">
      <div className="px-3 py-2 text-xs font-semibold text-default-500 border-b bg-default-100">
        {t('RELEASES')} ({countLabel})
      </div>
      <div className="flex-1 overflow-y-auto" onScroll={onScroll}>
        {releases.map((release) => (
          <ReleaseListItem
            key={release.id}
            release={release}
            isSelected={release.id === selectedReleaseId}
            onSelect={onSelect}
          />
        ))}
        {isLoadingMore ? (
          <div className="flex items-center justify-center gap-2 px-3 py-3 text-xs text-default-500">
            <Icon icon="mdi:loading" className="animate-spin" width="1em" height="1em" />
            {t('Loading more…')}
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface ReleaseDetailPaneProps {
  readonly customer: string;
  readonly release: ReleaseNotesEntry | undefined;
}

function ReleaseDetailPane({ customer, release }: ReleaseDetailPaneProps) {
  const { t } = useTranslation();

  if (!release) {
    return (
      <div className="flex-1 overflow-y-auto min-w-0 pr-6">
        <div className="p-6 text-sm text-default-500 text-center">{t('No releases available.')}</div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto min-w-0 pr-6">
      <div className="flex items-center gap-2 text-sm text-default-500 pb-2 border-b mb-3">
        <span className="font-semibold text-foreground">{customer}</span>
        <Icon icon="mdi:chevron-right" width="1em" height="1em" />
        <span className="font-semibold text-foreground">{release.name}</span>
        <span>
          {release.status === 'signed_off' ? t('signed off') : release.status}{' '}
          {formatSignedOffAt(release.signed_off_at)}
        </span>
      </div>
      <div className="text-sm text-default-700 prose prose-sm max-w-none markdown-body overflow-x-auto" style={MARKDOWN_CONTENT_STYLE}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {release.markdown}
        </ReactMarkdown>
      </div>
    </div>
  );
}

export function ReleaseNotesModal(props: ReleaseNotesModalProps) {
  const {
    isOpen,
    onClose,
    customer,
    releases,
    initialReleaseId,
    total,
    onDownloadWord,
    onDownloadPdf,
    onNotify,
    showDrafts,
    onToggleShowDrafts,
    hasMore,
    isLoadingMore,
    onLoadMore,
    tenantId,
    subscribersToken,
    subscribersBaseUrl,
  } = props;
  const { t } = useTranslation();
  const [view, setView] = useState<'releases' | 'subscribers'>('releases');

  useEffect(() => {
    if (isOpen) {
      setView('releases');
    }
  }, [isOpen]);

  const sortedReleases = useMemo(
    () =>
      [...releases].sort(
        (a, b) => new Date(b.signed_off_at).getTime() - new Date(a.signed_off_at).getTime()
      ),
    [releases]
  );

  const [selectedReleaseId, setSelectedReleaseId] = useState<string>(
    initialReleaseId ?? sortedReleases[0]?.id ?? ''
  );

  const selectedRelease =
    sortedReleases.find((release) => release.id === selectedReleaseId) ?? sortedReleases[0];

  const handleReleaseListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;
    const target = event.currentTarget;
    if (target.scrollHeight - target.scrollTop - target.clientHeight < LOAD_MORE_THRESHOLD_PX) {
      onLoadMore();
    }
  };

  const handleCopy = async (release: ReleaseNotesEntry) => {
    try {
      await navigator.clipboard.writeText(release.markdown);
      CtyShowToast(checkType.success, t('Release notes copied to clipboard'));
    } catch {
      CtyShowToast(checkType.error, t('Failed to copy release notes'));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="center"
      backdrop="blur"
      size="5xl"
      scrollBehavior="inside"
      classNames={{ base: 'max-w-6xl h-[85vh]' }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-3 pr-10">
          <span className="text-base font-semibold">{t('Release Notes')}</span>
          <ReleaseHeaderActions
            selectedRelease={selectedRelease}
            showDrafts={showDrafts}
            onToggleShowDrafts={onToggleShowDrafts}
            onDownloadWord={onDownloadWord}
            onDownloadPdf={onDownloadPdf}
            onNotify={onNotify}
            onCopy={(release) => void handleCopy(release)}
            showSubscribersButton={!!subscribersToken}
            onToggleSubscribers={() => setView(view === 'subscribers' ? 'releases' : 'subscribers')}
          />
        </ModalHeader>
        <ModalBody className="overflow-hidden pt-0 min-h-0 pr-0">
          {view === 'subscribers' && subscribersToken ? (
            <div className="h-full min-h-0 pr-6">
              <ReleaseNotesSubscribersPanel
                customer={customer}
                tenantId={tenantId}
                token={subscribersToken}
                baseUrl={subscribersBaseUrl}
                onBack={() => setView('releases')}
              />
            </div>
          ) : (
            <div className="flex gap-4 h-full min-h-0">
              <ReleaseListSidebar
                releases={sortedReleases}
                selectedReleaseId={selectedRelease?.id}
                total={total}
                isLoadingMore={isLoadingMore}
                onSelect={setSelectedReleaseId}
                onScroll={handleReleaseListScroll}
              />
              <ReleaseDetailPane customer={customer} release={selectedRelease} />
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default ReleaseNotesModal;
